import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Upload } from "lucide-react";
import { API, authHeaders, imgUrl, formatApiError } from "../../lib/api";

const inputCls =
  "w-full rounded-xl border border-[#DDD8C8] bg-white px-4 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";
const labelCls = "mb-1 block text-xs font-bold uppercase tracking-wider text-neutral-500";

const get = (obj, path) => path.split(".").reduce((o, k) => (o ? o[k] : ""), obj) ?? "";
const set = (obj, path, value) => {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  keys.slice(0, -1).forEach((k) => {
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  });
  cur[keys[keys.length - 1]] = value;
  return clone;
};

const Field = ({ label, path, form, onChange, textarea }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea rows={3} value={get(form, path)} onChange={(e) => onChange(set(form, path, e.target.value))} className={inputCls} data-testid={`content-field-${path.replace(/\./g, "-")}`} />
    ) : (
      <input value={get(form, path)} onChange={(e) => onChange(set(form, path, e.target.value))} className={inputCls} data-testid={`content-field-${path.replace(/\./g, "-")}`} />
    )}
  </div>
);

function ImageSection({ form, setForm, field, title, note, previewClass, testid, fallback = "/logo.png" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const value = form[field] || "";

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, authHeaders());
      setForm((f) => ({ ...f, [field]: data.path }));
      toast.success("Imagen subida. Pulse «Guardar cambios» para aplicarla.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[#E8E4D9] bg-white p-6 shadow-sm" data-testid={`${testid}-section`}>
      <h3 className="font-display mb-4 text-base font-bold text-neutral-900">{title}</h3>
      <div className="flex flex-wrap items-center gap-5">
        <div className={`flex items-center justify-center overflow-hidden rounded-xl border border-[#E8E4D9] bg-ivory ${previewClass}`} data-testid={`${testid}-preview`}>
          <img
            src={value ? imgUrl(value) : fallback}
            alt={title}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex-1 space-y-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} data-testid={`${testid}-file-input`} />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testid}-upload-button`}
            className="flex items-center gap-2 rounded-full border border-brand px-4 py-2 text-xs font-bold text-brand hover:bg-brand-light disabled:opacity-60"
          >
            <Upload size={13} /> {uploading ? "Subiendo…" : "Subir imagen"}
          </button>
          <input
            value={value.startsWith("http") ? value : ""}
            onChange={(e) => setForm({ ...form, [field]: e.target.value })}
            className={inputCls}
            placeholder="…o pegue una URL de imagen"
            data-testid={`${testid}-url-input`}
          />
          {value && (
            <button
              type="button"
              onClick={() => setForm({ ...form, [field]: "" })}
              data-testid={`${testid}-reset-button`}
              className="text-xs font-bold text-red-500 hover:underline"
            >
              Restaurar imagen original
            </button>
          )}
          <p className="text-[11px] text-neutral-400">{note}</p>
        </div>
      </div>
    </section>
  );
}

const GROUPS = [
  {
    title: "Hero (portada)",
    fields: [
      ["hero.overline", "Texto superior"],
      ["hero.title1", "Título línea 1"],
      ["hero.title2", "Título línea 2 (dorada)"],
      ["hero.title3", "Título línea 3"],
      ["hero.subtitle", "Subtítulo", true],
    ],
  },
  {
    title: "Líneas de productos",
    fields: [
      ["lines_intro.overline", "Texto superior"],
      ["lines_intro.title", "Título"],
      ["lines_intro.subtitle", "Subtítulo", true],
      ["line_descs.landerlan", "Descripción Línea Landerlan", true],
      ["line_descs.silver", "Descripción Silver Series", true],
      ["line_descs.gold", "Descripción Gold Series", true],
      ["line_descs.diamond", "Descripción Diamond Series", true],
    ],
  },
  {
    title: "Catálogo",
    fields: [
      ["catalog_intro.overline", "Texto superior"],
      ["catalog_intro.title", "Título"],
      ["catalog_intro.subtitle", "Subtítulo", true],
    ],
  },
  {
    title: "App móvil",
    fields: [
      ["app_section.title", "Título"],
      ["app_section.text", "Texto", true],
    ],
  },
  {
    title: "Calidad y autenticidad",
    fields: [
      ["quality.overline", "Texto superior"],
      ["quality.title", "Título"],
      ["quality.text", "Texto", true],
    ],
  },
  {
    title: "Nosotros",
    fields: [
      ["about.overline", "Texto superior"],
      ["about.title", "Título"],
      ["about.text", "Texto", true],
    ],
  },
  {
    title: "Contacto y pedidos",
    fields: [
      ["contact.overline", "Texto superior"],
      ["contact.title", "Título"],
      ["contact.subtitle", "Subtítulo", true],
      ["contact.address", "Dirección"],
      ["contact.phone", "Teléfono visible"],
      ["contact.schedule", "Horario"],
      ["contact.email", "Correo"],
    ],
  },
];

export default function ContentTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then((res) => setForm(res.data));
  }, []);

  if (!form)
    return <p className="py-16 text-center font-mono text-xs uppercase tracking-widest text-neutral-400">Cargando contenido…</p>;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, form, authHeaders());
      toast.success("Contenido actualizado. Recargue el sitio para ver los cambios.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="content-tab">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-neutral-900">Contenido de la web</h2>
          <p className="text-sm text-neutral-500">Edite el logo, los textos, datos de contacto y WhatsApp del sitio público.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button"
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark disabled:opacity-60"
        >
          <Save size={15} /> {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>

      <div className="space-y-6">
        <ImageSection
          form={form}
          setForm={setForm}
          field="logo"
          title="Logo del sitio"
          note="Se usa en la cabecera, el footer, el panel y el favicon. Recomendado: PNG con fondo transparente."
          previewClass="h-24 w-48 p-3 [&_img]:object-contain"
          testid="logo"
        />
        <ImageSection
          form={form}
          setForm={setForm}
          field="hero_image"
          title="Imagen de portada (hero)"
          note="Fondo grande de la portada. Recomendado: foto horizontal de alta resolución (mínimo 1600px de ancho)."
          previewClass="h-24 w-48"
          testid="hero-image"
          fallback="/images/hero-banner.jpg"
        />
        <ImageSection
          form={form}
          setForm={setForm}
          field="about_image"
          title="Imagen de Nosotros"
          note="Foto vertical de la sección Nosotros (compromiso con República Dominicana)."
          previewClass="h-24 w-48"
          testid="about-image"
          fallback="https://images.unsplash.com/photo-1554344728-77cf90d9ed26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxmaXRuZXNzJTIwYm9keWJ1aWxkZXIlMjB3b3Jrb3V0JTIwZ3ltJTIwdHJhaW5pbmd8ZW58MHx8fGJsYWNrfDE3OTMwMjE4Mjd8MA&ixlib=rb-4.1.0&q=85"
        />
        <ImageSection
          form={form}
          setForm={setForm}
          field="quality_image"
          title="Imagen de Calidad"
          note="Imagen de la sección Calidad y autenticidad (producto con QR o laboratorio)."
          previewClass="h-24 w-48"
          testid="quality-image"
          fallback="https://landerpharma.com.py/rails/active_storage/blobs/redirect/eyJfcmFpbHMiOnsiZGF0YSI6MTg4LCJwdXIiOiJibG9iX2lkIn19--5d9d61deb75088a0133a3ac7970d94ecfb641fa8/HCG.png"
        />
        <ImageSection
          form={form}
          setForm={setForm}
          field="app_image"
          title="Imagen de la App"
          note="Captura o mockup de la app móvil en la sección App Landerlan."
          previewClass="h-24 w-48"
          testid="app-image"
          fallback="https://landerpharma.com.py/rails/active_storage/blobs/redirect/eyJfcmFpbHMiOnsiZGF0YSI6MTQsInB1ciI6ImJsb2JfaWQifX0=--c66d477d381de3179ce35adcd0a42d2151230b62/mobile_image.png"
        />

        <section className="rounded-2xl border border-[#E8E4D9] bg-white p-6 shadow-sm">
          <h3 className="font-display mb-4 text-base font-bold text-neutral-900">WhatsApp de pedidos</h3>
          <label className={labelCls}>Número de WhatsApp (código de país + número, solo dígitos)</label>
          <input
            value={form.whatsapp_number || ""}
            onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value.replace(/\D/g, "") })}
            className={inputCls}
            placeholder="18295067111"
            data-testid="content-field-whatsapp-number"
          />
        </section>

        <section className="rounded-2xl border border-[#E8E4D9] bg-white p-6 shadow-sm">
          <h3 className="font-display mb-4 text-base font-bold text-neutral-900">Marquee (cinta de texto)</h3>
          <label className={labelCls}>Frases (una por línea)</label>
          <textarea
            rows={6}
            value={(form.marquee || []).join("\n")}
            onChange={(e) => setForm({ ...form, marquee: e.target.value.split("\n").filter((l) => l.trim()) })}
            className={inputCls}
            data-testid="content-field-marquee"
          />
        </section>

        <section className="rounded-2xl border border-[#E8E4D9] bg-white p-6 shadow-sm">
          <h3 className="font-display mb-4 text-base font-bold text-neutral-900">Estadísticas del hero</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {(form.stats || []).map((s, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={s.value}
                  onChange={(e) => {
                    const stats = [...form.stats];
                    stats[i] = { ...stats[i], value: e.target.value };
                    setForm({ ...form, stats });
                  }}
                  className={inputCls}
                  placeholder="Valor"
                  data-testid={`content-field-stat-${i}-value`}
                />
                <input
                  value={s.label}
                  onChange={(e) => {
                    const stats = [...form.stats];
                    stats[i] = { ...stats[i], label: e.target.value };
                    setForm({ ...form, stats });
                  }}
                  className={inputCls}
                  placeholder="Etiqueta"
                  data-testid={`content-field-stat-${i}-label`}
                />
              </div>
            ))}
          </div>
        </section>

        {GROUPS.map((g) => (
          <section key={g.title} className="rounded-2xl border border-[#E8E4D9] bg-white p-6 shadow-sm">
            <h3 className="font-display mb-4 text-base font-bold text-neutral-900">{g.title}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {g.fields.map(([path, label, textarea]) => (
                <div key={path} className={textarea ? "sm:col-span-2" : ""}>
                  <Field label={label} path={path} form={form} onChange={setForm} textarea={textarea} />
                </div>
              ))}
            </div>
          </section>
        ))}

        {[
          { key: "quality", title: "Puntos destacados de calidad", listKey: "highlights" },
          { key: "about", title: "Pilares de Nosotros", listKey: "pillars" },
        ].map(({ key, title, listKey }) => (
          <section key={key} className="rounded-2xl border border-[#E8E4D9] bg-white p-6 shadow-sm">
            <h3 className="font-display mb-4 text-base font-bold text-neutral-900">{title}</h3>
            <div className="space-y-4">
              {(form[key]?.[listKey] || []).map((item, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-3">
                  <input
                    value={item.title}
                    onChange={(e) => {
                      const list = [...form[key][listKey]];
                      list[i] = { ...list[i], title: e.target.value };
                      setForm(set(form, `${key}.${listKey}`, list));
                    }}
                    className={inputCls}
                    placeholder="Título"
                    data-testid={`content-field-${key}-${listKey}-${i}-title`}
                  />
                  <textarea
                    rows={2}
                    value={item.desc}
                    onChange={(e) => {
                      const list = [...form[key][listKey]];
                      list[i] = { ...list[i], desc: e.target.value };
                      setForm(set(form, `${key}.${listKey}`, list));
                    }}
                    className={`${inputCls} sm:col-span-2`}
                    placeholder="Descripción"
                    data-testid={`content-field-${key}-${listKey}-${i}-desc`}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}

        <section className="rounded-2xl border border-[#E8E4D9] bg-white p-6 shadow-sm">
          <h3 className="font-display mb-4 text-base font-bold text-neutral-900">Aviso legal del footer</h3>
          <textarea
            rows={3}
            value={form.footer_legal || ""}
            onChange={(e) => setForm({ ...form, footer_legal: e.target.value })}
            className={inputCls}
            data-testid="content-field-footer-legal"
          />
        </section>

        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button-bottom"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-white hover:bg-brand-dark disabled:opacity-60"
        >
          <Save size={15} /> {saving ? "Guardando…" : "Guardar todos los cambios"}
        </button>
      </div>
    </div>
  );
}
